/**
 * セット商品 (product_bundles) の hooks。Phase A/B。
 */
import type {
  ProductAudienceKind,
  ProductBundle,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface ProductBundleRecord extends ProductBundle {
  id: string;
}

export function useProductBundles() {
  return useQuery({
    queryKey: ['product_bundles'],
    queryFn: async (): Promise<ProductBundleRecord[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'product_bundles'),
          orderBy('isActive', 'desc'),
          orderBy('validFrom', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ProductBundle),
      }));
    },
  });
}

export function useProductBundle(id: string | null | undefined) {
  return useQuery({
    queryKey: ['product_bundles', 'detail', id],
    enabled: !!id,
    queryFn: async (): Promise<ProductBundleRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'product_bundles', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as ProductBundle) };
    },
  });
}

interface UpsertBundleInput {
  id?: string;
  name: string;
  description?: string | null;
  items: Array<{ productId: string; quantity: number }>;
  bundlePriceExcludingTax: number;
  bundlePriceIncludingTax: number;
  validFrom: string;
  validUntil?: string | null;
  category: string;
  audienceKind: ProductAudienceKind;
  isActive: boolean;
}

const upsertBundleFn = httpsCallable<UpsertBundleInput, { id: string }>(
  functions,
  'upsertProductBundle',
);

export function useUpsertProductBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertBundleInput) => {
      const r = await upsertBundleFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product_bundles'] }),
  });
}

const deleteBundleFn = httpsCallable<{ id: string }, { id: string }>(
  functions,
  'deleteProductBundle',
);

export function useDeleteProductBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await deleteBundleFn({ id });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product_bundles'] }),
  });
}
